/**
 * 유사 문제 결과가 들어올 자리 — 순수 UI. 원본 카드 바로 아래에 서고, 실패해도 같은 자리에서 실패 카드로 바뀐다.
 * 헤더의 휴지통이 곧 취소(생성 중)/폐기(실패)다 — 별도 취소 버튼은 없다(핸드오프 #5·8).
 * 높이는 헤더 28 + 제목 행 30 + gap 24 + 본문 영역 180 + pb 24 로 Figma 카드(286)와 같다.
 */
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { CaretDownIcon, ExclamationmarkSmallCircleIcon, MenuSmallIcon, SparklingFillIcon, TrashIcon } from '@/icons';
import { Button, IconButton, Tooltip } from '@/ui_v3';
import { QuizCard } from '../../../components/EditableQuizCard/QuizCardUI';
import { QuizTypeSelectorButton } from '../../../components/EditableQuizCard/QuizTypeSelector/QuizTypeSelector';
import { type QuizType } from '../../../components/type';
import { useQuestionCardAiCopy } from '../hooks/useQuestionCardAiCopy';
import { SimilarQuestionWash } from './SimilarQuestionWash';
export type SimilarQuestionPendingCardUIProps = {
  /** 원본 카드 id — 스크롤·포커스 대상 식별용 DOM id 에 쓰인다. */
  quizId: string;
  status: 'running' | 'error';
  /** 이 카드가 들어갈 문항 번호(1부터). */
  number: number;
  /** 생성 중에만 그리는 유형 칩. 실패 카드에는 칩이 없다. */
  type: QuizType;
  /** 생성 중이면 요청 취소 + 카드 제거, 실패면 카드 제거. */
  onDelete: () => void;
  onRetry: () => void;
  /**
   * 재시도를 지금 할 수 없는 사유(다른 AI 작업 진행 중 · 원본 자격 미달). 있으면 버튼을 잠그고
   * 사유를 툴팁으로 보인다 — 메뉴 항목의 비활성 사유 표시(PRD §7-2)와 같은 방식.
   */
  retryBlockedReason?: string;
};
const PROGRESS_AREA_CLASS = 'flex h-[180px] w-full flex-col items-center justify-center gap-sm';
const RunningArea = () => {
  const copy = useQuestionCardAiCopy();
  return <div className={`${PROGRESS_AREA_CLASS} relative overflow-hidden rounded-2xs bg-default`} data-sentry-component="RunningArea" data-sentry-source-file="SimilarQuestionPendingCardUI.tsx">
      <SimilarQuestionWash data-sentry-element="SimilarQuestionWash" data-sentry-source-file="SimilarQuestionPendingCardUI.tsx" />
      {/* 보이는 문구가 상태를 말해 주므로 아이콘은 장식이다. */}
      <SparklingFillIcon size="lg" fill="#51A4D4" aria-hidden className="relative" data-sentry-element="SparklingFillIcon" data-sentry-source-file="SimilarQuestionPendingCardUI.tsx" />
      {/* 그라디언트 텍스트 토큰은 디자이너 확인 대기 — 핸드오프의 raw 값(165° #8478FF 13% → #18D0B4 97%). */}
      <span className="relative bg-[linear-gradient(165deg,#8478FF_13%,#18D0B4_97%)] bg-clip-text text-body-lg-semibold text-transparent">
        {copy.status.similar}
      </span>
    </div>;
};
const RetryButton = ({
  onRetry,
  retryBlockedReason
}: Pick<SimilarQuestionPendingCardUIProps, 'onRetry' | 'retryBlockedReason'>) => {
  const copy = useQuestionCardAiCopy();
  const button = <Button size="xs" variant="outlined" intent="neutral" rounded="full" disabled={!!retryBlockedReason} onClick={onRetry}>
      {copy.error.retry}
    </Button>;
  if (!retryBlockedReason) return button;
  // 비활성 버튼은 포인터 이벤트를 받지 않아 툴팁 트리거를 span 으로 감싼다.
  return <Tooltip.Provider data-sentry-element="unknown" data-sentry-component="RetryButton" data-sentry-source-file="SimilarQuestionPendingCardUI.tsx">
      <Tooltip.Root data-sentry-element="unknown" data-sentry-source-file="SimilarQuestionPendingCardUI.tsx">
        <Tooltip.Trigger asChild data-sentry-element="unknown" data-sentry-source-file="SimilarQuestionPendingCardUI.tsx">
          <span className="inline-flex">{button}</span>
        </Tooltip.Trigger>
        <Tooltip.Content data-sentry-element="unknown" data-sentry-source-file="SimilarQuestionPendingCardUI.tsx">{retryBlockedReason}</Tooltip.Content>
      </Tooltip.Root>
    </Tooltip.Provider>;
};
const ErrorArea = ({
  onRetry,
  retryBlockedReason
}: Pick<SimilarQuestionPendingCardUIProps, 'onRetry' | 'retryBlockedReason'>) => {
  const copy = useQuestionCardAiCopy();
  return <div role="alert" className={PROGRESS_AREA_CLASS} data-sentry-component="ErrorArea" data-sentry-source-file="SimilarQuestionPendingCardUI.tsx">
      <ExclamationmarkSmallCircleIcon size="lg" fill={ICON_COLOR.alternative} aria-hidden data-sentry-element="ExclamationmarkSmallCircleIcon" data-sentry-source-file="SimilarQuestionPendingCardUI.tsx" />
      <span className="text-body-lg-semibold text-alternative">
        {copy.status.similarFailed}
      </span>
      <RetryButton onRetry={onRetry} retryBlockedReason={retryBlockedReason} data-sentry-element="RetryButton" data-sentry-source-file="SimilarQuestionPendingCardUI.tsx" />
    </div>;
};
export const SimilarQuestionPendingCardUI = ({
  quizId,
  status,
  number,
  type,
  onDelete,
  onRetry,
  retryBlockedReason
}: SimilarQuestionPendingCardUIProps) => {
  const {
    t
  } = useTranslation(['build', 'common']);
  const isRunning = status === 'running';
  return <QuizCard.Root id={`${quizId}-similar-slot`} className="w-full max-w-[650px]" aria-live="polite" data-sentry-element="unknown" data-sentry-component="SimilarQuestionPendingCardUI" data-sentry-source-file="SimilarQuestionPendingCardUI.tsx">
      {/* 드래그 핸들 모양만 — 결과가 오기 전엔 옮길 수 없다. */}
      <div className="flex w-full justify-center py-3xs" aria-hidden>
        <MenuSmallIcon size="sm" className="rotate-90" fill={ICON_COLOR.alternative} data-sentry-element="MenuSmallIcon" data-sentry-source-file="SimilarQuestionPendingCardUI.tsx" />
      </div>
      <QuizCard.Content data-sentry-element="unknown" data-sentry-source-file="SimilarQuestionPendingCardUI.tsx">
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-row items-center gap-sm text-heading-sm-bold">
            {t('build:build.quiz.questionNumber', {
            number
          })}
            {isRunning && <QuizTypeSelectorButton selectedType={type} disabled tabIndex={-1} className="pointer-events-none text-body-sm-bold opacity-[0.64]" rightSlot={<CaretDownIcon size="xs" />} />}
          </div>
          <IconButton size="xs" variant="outlined" intent="neutral" aria-label={t('common:global.delete')} onClick={onDelete} data-sentry-element="IconButton" data-sentry-source-file="SimilarQuestionPendingCardUI.tsx">
            <TrashIcon size="xs" fill={ICON_COLOR.default} data-sentry-element="TrashIcon" data-sentry-source-file="SimilarQuestionPendingCardUI.tsx" />
          </IconButton>
        </div>
        {isRunning ? <RunningArea /> : <ErrorArea onRetry={onRetry} retryBlockedReason={retryBlockedReason} />}
      </QuizCard.Content>
    </QuizCard.Root>;
};